/**
 * Runtime mock mode: the real app served by the MSW handlers in
 * `mocks/handlers.ts` with a fake signed-in user. Resolution order:
 * build-time `NEXT_PUBLIC_MOCK_MODE`, then the localStorage flag (only where
 * `isRuntimeMockModeAllowed`). Architecture: `.claude/docs/web-mock-mode.md`.
 */
import { BOOT_HINT_STORAGE_KEY } from "./bootHint";
import { isMockModeAllowedForBackend } from "./mockModeBuildGuard.mjs";

export const MOCK_MODE_STORAGE_KEY = "up-mock-mode";

/** Where `public/mockServiceWorker.js` is served (MSW `serviceWorker.url`). */
export const MOCK_SERVICE_WORKER_URL = "/mockServiceWorker.js";

/**
 * MSW's own worker match (`findWorker` default in msw/lib/browser): the
 * worker's `scriptURL` equals the absolute worker URL — exact, not a
 * substring, so an unrelated `/x/mockServiceWorker.js.bak` is not ours.
 */
export function isMockServiceWorkerUrl(
  scriptURL: string | undefined,
  href: string = window.location.href
): boolean {
  return scriptURL === new URL(MOCK_SERVICE_WORKER_URL, href).href;
}
export const MOCK_MODE_QUERY_PARAM = "mockMode";

/**
 * localStorage keys that can hold ids minted by the mock handlers. Left
 * behind, they would point a real session at resources that don't exist.
 */
const MOCK_SESSION_KEYS = [
  // CustomerAccountProvider's selected account (a fixture account id).
  "customerAccountId",
  // useOnboardingStore's persisted applicationId/type (a mock application).
  "onboarding-storage",
  // The boot prefetch hint's last-viewed account (a fixture account UUID).
  BOOT_HINT_STORAGE_KEY,
];

/**
 * sessionStorage keys holding mock-derived state. The exit reload stays in the
 * same tab, so these survive it unless removed.
 */
const MOCK_SESSION_STORAGE_KEYS = [
  // PricingProvider's back-navigation snapshot (mock pricing request + ids).
  "exchangeState",
  // Tells PricingProvider to restore that snapshot on the next mount.
  "navigatingBack",
];

// `window.localStorage` itself throws (SecurityError) when storage is blocked
// — sandboxed iframes, some privacy settings. Treat that as "no storage".
const defaultStorage = (): Storage | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const defaultSessionStorage = (): Storage | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

/**
 * Allow-list (`lib/mockModeBuildGuard.mjs`): only `uat`/`dev` backends, never
 * the `production` platform. Both values are inlined at build time by
 * next.config.mjs, so this is a build-time constant in the browser bundle.
 */
export function isRuntimeMockModeAllowed(
  backendEnv: string | undefined = process.env.NEXT_PUBLIC_BACKEND_ENVIRONMENT,
  backendPlatform: string | undefined = process.env.NEXT_PUBLIC_BACKEND_PLATFORM
): boolean {
  return isMockModeAllowedForBackend({ backendEnv, backendPlatform });
}

/**
 * Build-time `NEXT_PUBLIC_MOCK_MODE`, never on a disallowed backend
 * (next.config.mjs also refuses to build that combination). A function, not a
 * constant, so the check reads the same env the rest of this module does.
 */
export function isBuildTimeMockMode(): boolean {
  return (
    process.env.NEXT_PUBLIC_MOCK_MODE === "true" && isRuntimeMockModeAllowed()
  );
}

/**
 * Build-time flag, else the stored flag. Short-circuits to `false` where mock
 * mode isn't allowed BEFORE touching storage, and reads unreadable storage
 * (blocked, or `getItem` throwing) as "off". `null` means "no storage" (the
 * server); omitted means the page's localStorage.
 */
export function isMockModeActive(storage?: Storage | null): boolean {
  if (!isRuntimeMockModeAllowed()) return false;
  if (isBuildTimeMockMode()) return true;
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return false;
  try {
    return store.getItem(MOCK_MODE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export type MockModeParamAction = "enable" | "disable" | null;

export function readMockModeParam(search: string): MockModeParamAction {
  const value = new URLSearchParams(search).get(MOCK_MODE_QUERY_PARAM);
  if (value === "true") return "enable";
  if (value === "false") return "disable";
  return null;
}

export function enableMockMode(storage = defaultStorage()): void {
  storage?.setItem(MOCK_MODE_STORAGE_KEY, "true");
}

// Snapshot first: removing while indexing would skip keys.
const storageKeys = (storage: Storage): string[] =>
  Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
    (key): key is string => key !== null
  );

const removeKeysWhere = (
  storage: Storage,
  predicate: (key: string) => boolean
) => {
  for (const key of storageKeys(storage)) {
    if (predicate(key)) storage.removeItem(key);
  }
};

const isOidcKey = (key: string) => key.startsWith("oidc.");

/**
 * Drop every stored oidc-client-ts entry (`oidc.user:*` tokens, `oidc.*`
 * signin state). Run when mock mode boots, so a developer's real session can
 * never be read by `baseQuery` or picked up by a UserManager, and when it
 * exits, so the fake session never outlives it.
 */
export function clearOidcSession(storage = defaultStorage()): void {
  if (storage) removeKeysWhere(storage, isOidcKey);
}

/**
 * Leaves mock mode: drops the flag FIRST (so even a half-finished cleanup
 * boots the next load in real mode), then every oidc.* entry, the mock-derived
 * ids and the MSW registration. Each step runs on its own — one failing never
 * skips the rest — and any failures are rethrown together as an
 * `AggregateError` once everything has been attempted.
 */
export async function cleanUpMockSession({
  storage = defaultStorage(),
  sessionStorage = defaultSessionStorage(),
  serviceWorker = typeof navigator === "undefined"
    ? undefined
    : navigator.serviceWorker,
}: {
  storage?: Storage;
  sessionStorage?: Storage;
  serviceWorker?: ServiceWorkerContainer;
} = {}): Promise<void> {
  const errors: unknown[] = [];
  const attempt = (step: () => void) => {
    try {
      step();
    } catch (error) {
      errors.push(error);
    }
  };

  if (storage) {
    attempt(() => storage.removeItem(MOCK_MODE_STORAGE_KEY));
    attempt(() => {
      for (const key of storageKeys(storage))
        if (isOidcKey(key)) attempt(() => storage.removeItem(key));
    });
    for (const key of MOCK_SESSION_KEYS) attempt(() => storage.removeItem(key));
  }
  if (sessionStorage) {
    for (const key of MOCK_SESSION_STORAGE_KEYS)
      attempt(() => sessionStorage.removeItem(key));
  }
  if (serviceWorker) {
    try {
      const registrations = await serviceWorker.getRegistrations();
      const results = await Promise.allSettled(
        registrations
          .filter((r) =>
            // Deliberately broader than isMockServiceWorkerUrl: on the way
            // out, any leftover mock worker (another path, a query string)
            // should go too.
            (r.active ?? r.waiting ?? r.installing)?.scriptURL.includes(
              "mockServiceWorker"
            )
          )
          .map(async (r) => {
            if (!(await r.unregister()))
              console.warn(
                "[mock-mode] The mock service worker registration could not be unregistered — it passes requests through while no page activates it, but clear it in DevTools → Application → Service workers."
              );
          })
      );
      for (const result of results)
        if (result.status === "rejected") errors.push(result.reason);
    } catch (error) {
      errors.push(error);
    }
  }

  if (errors.length > 0)
    throw new AggregateError(
      errors,
      `[mock-mode] Mock session cleanup failed (${errors.length} step${errors.length === 1 ? "" : "s"})`
    );
}

// ---------------------------------------------------------------------------
// Per-page decision latch
// ---------------------------------------------------------------------------

/**
 * What MockModeGate decided for THIS page load: `true` mock, `false` real,
 * `undefined` not decided yet. Request-time code (`baseQuery`) reads this, not
 * the storage flag: the flag is shared across tabs and can flip under a live
 * page (another tab enabling or leaving mock mode), the decision cannot. A
 * mock decision is sticky — nothing can downgrade a mock page to "real" and
 * have it start attaching real tokens.
 */
let mockModeDecision: boolean | undefined;
let warnedUndecided = false;

export function setMockModeDecision(isMock: boolean): void {
  mockModeDecision = mockModeDecision === true || isMock;
}

export function getMockModeDecision(): boolean | undefined {
  return mockModeDecision;
}

/** Test-only: forget the page's decision between specs. */
export function resetMockModeDecisionForTests(): void {
  mockModeDecision = undefined;
  warnedUndecided = false;
}

/**
 * Whether a request from this page is a mock-mode request. Undecided means
 * real: children of the gate don't mount before it decides, so an undecided
 * read is a request from outside the gate (Storybook, a stray module-level
 * call). Warned about once where it could matter — never on a disallowed
 * (prd) build, never under test.
 */
export function isMockModeRequest(): boolean {
  if (mockModeDecision !== undefined) return mockModeDecision;
  if (
    !warnedUndecided &&
    process.env.NODE_ENV !== "test" &&
    process.env.NEXT_PUBLIC_IS_TEST_ENV !== "true" &&
    isRuntimeMockModeAllowed()
  ) {
    warnedUndecided = true;
    console.warn(
      "[mock-mode] A request was made before MockModeGate decided mock vs real mode — treating it as real mode."
    );
  }
  return false;
}

// ---------------------------------------------------------------------------
// Re-activation request
// ---------------------------------------------------------------------------

/** `window` event MockModeGate listens for to re-assert mocking. */
export const MOCK_REACTIVATE_EVENT = "up-mock-reactivate";

/**
 * Ask MockModeGate to re-assert mocking now. `baseQuery` calls this when a
 * mock-mode BFF call gets no response at all (`FETCH_ERROR`): the worker
 * answers every one, so a status-less failure means it has lost this page
 * (restart/update), and a tab that stays visible would otherwise wait for
 * the next `visibilitychange`. The gate coalesces a burst into one.
 */
export function requestMockReactivation(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MOCK_REACTIVATE_EVENT));
}
